<template>
  <button ref="trigger"
    type="button"
    :class="['field__control field__control--select', { 'field__control--empty': !current || current.muted }]"
    role="combobox"
    aria-haspopup="listbox"
    :aria-expanded="open ? 'true' : 'false'"
    :aria-controls="listId"
    :aria-activedescendant="open && active >= 0 ? listId + '-' + active : undefined"
    :disabled="disabled || loading"
    @click="toggle"
    @keydown="onKeydown"
    @blur="close">
    {{ loading ? 'Laadimine…' : current ? current.label : placeholder }}
    <Teleport to="body">
      <!-- mousedown.prevent keeps the focus on the trigger, so picking does not blur it closed. -->
      <ul v-if="open"
        :id="listId"
        ref="panel"
        :class="['select-menu', { 'select-menu--up': up }]"
        :style="panelStyle"
        role="listbox"
        @mousedown.prevent>
        <template v-for="(item, i) in items"
          :key="item.key">
          <li v-if="item.header"
            class="select-menu__header"
            role="presentation">{{ item.label }}</li>
          <li v-else
            :id="listId + '-' + i"
            :class="['select-menu__option', { 'is-active': i === active, 'select-menu__option--muted': item.muted, 'select-menu__option--indent': item.indent }]"
            role="option"
            :aria-selected="item.key === selectedKey ? 'true' : 'false'"
            @pointermove="active = i"
            @click="pick(item)">
            <span>{{ item.label }}</span>
            <i v-if="item.key === selectedKey"
              class="fa-solid fa-check"
              aria-hidden="true" />
          </li>
        </template>
        <li v-if="!items.length"
          class="select-menu__empty"
          role="presentation">Valikuid pole</li>
      </ul>
    </Teleport>
  </button>
</template>

<script>
// The look the panel takes from its field, so it matches the admin themes and the dark contact dialog alike.
const TOKENS = ['--text', '--text-muted', '--text-disabled', '--accent', '--field-fill', '--border-translucent']
const GAP = 6
const MAX_HEIGHT = 320

let uid = 0

// Field's dropdown, in place of a native <select> whose open list the OS draws. Items: { key, label, header?, indent?, muted? }.
export default {
  name: 'select-menu',
  emits: ['pick'],
  inject: { requestAutoSave: { default: null } },
  props: {
    items: { type: Array, default: () => [] },
    selectedKey: { type: [String, Number], default: null },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  data() {
    return { open: false, active: -1, up: false, panelStyle: {}, listId: `select-menu-${++uid}`, typed: '', typedAt: 0 }
  },
  computed: {
    current() {
      return this.items.find((item) => !item.header && item.key === this.selectedKey) || null
    },
  },
  beforeUnmount() {
    this.unlisten()
    clearTimeout(this.typedTimer)
  },
  methods: {
    toggle() {
      if (this.open) this.close()
      else this.show()
    },
    show() {
      if (this.open || this.disabled || this.loading) return
      const selected = this.items.findIndex((item) => !item.header && item.key === this.selectedKey)
      this.active = selected >= 0 ? selected : this.step(-1, 1)
      this.open = true
      this.place()
      window.addEventListener('scroll', this.place, true)
      window.addEventListener('resize', this.place)
      // Safari does not focus a tapped button, so the blur that closes the list never comes.
      document.addEventListener('pointerdown', this.onOutside, true)
      this.$nextTick(this.reveal)
    },
    close() {
      if (!this.open) return
      this.open = false
      this.unlisten()
    },
    unlisten() {
      window.removeEventListener('scroll', this.place, true)
      window.removeEventListener('resize', this.place)
      document.removeEventListener('pointerdown', this.onOutside, true)
    },
    onOutside(e) {
      if (!this.$refs.trigger?.contains(e.target) && !this.$refs.panel?.contains(e.target)) this.close()
    },
    pick(item) {
      this.close()
      this.$refs.trigger?.focus()
      this.$emit('pick', item)
      this.requestAutoSave?.()
    },
    // Under the capsule, or above it when the room below is short; the trigger's colours go along.
    place(e) {
      if (e && e.target === this.$refs.panel) return
      const trigger = this.$refs.trigger
      if (!trigger) return
      const rect = (trigger.closest('.field__frame') || trigger).getBoundingClientRect()
      const below = window.innerHeight - rect.bottom - GAP * 2
      const above = rect.top - GAP * 2
      this.up = below < Math.min(MAX_HEIGHT, 200) && above > below
      const cs = getComputedStyle(trigger)
      const left = Math.max(GAP, rect.left)
      const style = {
        left: `${left}px`,
        minWidth: `${Math.min(rect.width, window.innerWidth - left - GAP)}px`,
        maxWidth: `${window.innerWidth - left - GAP}px`,
        maxHeight: `${Math.min(MAX_HEIGHT, this.up ? above : below)}px`,
        fontFamily: cs.fontFamily,
        fontSize: cs.fontSize,
        colorScheme: cs.colorScheme,
      }
      if (this.up) style.bottom = `${window.innerHeight - rect.top + GAP}px`
      else style.top = `${rect.bottom + GAP}px`
      for (const token of TOKENS) style[token] = cs.getPropertyValue(token).trim()
      this.panelStyle = style
    },
    reveal() {
      const panel = this.$refs.panel
      const el = panel?.querySelector('.is-active')
      if (!el) return
      if (el.offsetTop < panel.scrollTop) panel.scrollTop = el.offsetTop - GAP
      else if (el.offsetTop + el.offsetHeight > panel.scrollTop + panel.clientHeight) panel.scrollTop = el.offsetTop + el.offsetHeight - panel.clientHeight + GAP
    },
    // The next pickable item from `from` in direction `dir`; headers are skipped. Stays put at either end.
    step(from, dir) {
      for (let i = from + dir; i >= 0 && i < this.items.length; i += dir) if (!this.items[i].header) return i
      return from
    },
    moveTo(i) {
      this.active = i
      this.$nextTick(this.reveal)
    },
    onKeydown(e) {
      const { key } = e
      if (!this.open) {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(key)) {
          e.preventDefault()
          this.show()
        } else if (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          this.show()
          this.typeAhead(key)
        }
        return
      }
      if (key === 'ArrowDown' || key === 'ArrowUp') {
        e.preventDefault()
        this.moveTo(this.step(this.active, key === 'ArrowDown' ? 1 : -1))
      } else if (key === 'Home' || key === 'End') {
        e.preventDefault()
        this.moveTo(key === 'Home' ? this.step(-1, 1) : this.step(this.items.length, -1))
      } else if (key === 'Enter' || (key === ' ' && !this.typed)) {
        e.preventDefault()
        const item = this.items[this.active]
        if (item && !item.header) this.pick(item)
      } else if (key === 'Escape') {
        // Only the list closes, not the dialog around it.
        e.preventDefault()
        e.stopPropagation()
        this.close()
      } else if (key === 'Tab') {
        this.close()
      } else if (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault()
        this.typeAhead(key)
      }
    },
    // Letters typed in quick succession jump to the first item that starts with them, as a native select does.
    typeAhead(key) {
      const now = Date.now()
      this.typed = (now - this.typedAt > 700 ? '' : this.typed) + key.toLowerCase()
      this.typedAt = now
      clearTimeout(this.typedTimer)
      this.typedTimer = setTimeout(() => { this.typed = '' }, 700)
      const hit = this.items.findIndex((item) => !item.header && String(item.label).toLowerCase().startsWith(this.typed))
      if (hit >= 0) this.moveTo(hit)
    },
  },
}
</script>

<style lang="scss" src="./select-menu.scss"></style>
